import { useEffect, type FC } from 'react';
import { Typography, Skeleton, Link, InlineNotification } from '@packages/shared';
import { useConfig } from '@packages/utilities';
import { CheckmarkInverted20, PaybackColored32 } from '@packages/themes/icons';
import { defineMessages, FormattedNumber, useIntl } from 'react-intl';
import { type FragmentType, getFragmentData } from '@packages/gql/generated/shopping';
import { BasketFragmentFragmentDoc } from '@packages/gql/generated/shopping/BasketFragmentFragmentDoc';
import { AddLoyaltyIdentifierDocument } from '@packages/gql/generated/shopping/AddLoyaltyIdentifierDocument';
import { LoyaltyFragmentFragmentDoc } from '@packages/gql/generated/shopping/LoyaltyFragmentFragmentDoc';
import { useAuthMutation } from '@packages/gql';
import { useEventTracking } from '../Tracking/useEventTracking';

export type PaybackBoxProps = {
  loyaltyProgramData: FragmentType<typeof LoyaltyFragmentFragmentDoc>;
};

export const messages = defineMessages({
  headline: {
    id: 'PaybackBox.Headline',
    defaultMessage: 'PAYBACK PUNKTE',
  },
  defaultDescription: {
    id: 'PaybackBox.Default.Description',
    defaultMessage:
      'Du kannst bei deinem Einkauf {points} sammeln. Trage dazu deine PAYBACK Kundennummer in Schritt 5 ein.',
  },
  loginLink: {
    id: 'PaybackBox.Login.Link',
    defaultMessage: 'Noch kein PAYBACK? Jetzt anmelden!',
  },
  cardPresent: {
    id: 'PaybackBox.CardPresent',
    defaultMessage: 'Deine PAYBACK Kundennummer ist hinterlegt.',
  },
  cardNumberWarningMsg: {
    id: 'PaybaykBox.cardNumber.warning',
    defaultMessage: 'Deine Kundennummer konnte nicht von PAYBACK übernommen werden. Bitte prüfen.',
  },
});

type ScridItem = {
  createdAt: number; // unix timestamp: when the item has been created
  expiresAt: number;
  value: string; // the actual scrid
};

const readScrid = (): ScridItem | null => {
  const item = localStorage.getItem('scrid');

  try {
    const scridItem = item ? (JSON.parse(item) as ScridItem) : null;
    if (scridItem && scridItem.expiresAt < Date.now()) {
      localStorage.removeItem('scrid');
      return null;
    }

    return scridItem;
  } catch (err) {
    return null;
  }
};

export const PaybackBox: FC<PaybackBoxProps> = ({ loyaltyProgramData }: PaybackBoxProps) => {
  const { formatMessage, locale } = useIntl();
  const {
    order: { payback },
  } = useConfig();
  const { basePoints, connected } = getFragmentData(LoyaltyFragmentFragmentDoc, loyaltyProgramData);
  const dispatch = useEventTracking();
  const [state, addLoyaltyIdentifier] = useAuthMutation(AddLoyaltyIdentifierDocument);
  const mutationResult = getFragmentData(
    BasketFragmentFragmentDoc,
    state.data?.addLoyaltyIdentifierToBasket,
  );

  useEffect(() => {
    const scrid = readScrid();
    if (scrid) {
      const addLoyaltyIdentifierFromScrid = async () => {
        // IMPORTANT: remove scrid before calling the mutation otherwise this is an endless loop
        // because the mutation triggers a rerendering of this component
        localStorage.removeItem('scrid');
        const result = await addLoyaltyIdentifier({
          locale,
          identifier: scrid.value,
          identifierType: 'Scrid',
        });
        const resultData = getFragmentData(
          BasketFragmentFragmentDoc,
          result.data?.addLoyaltyIdentifierToBasket,
        );
        if (resultData && resultData.success) {
          dispatch('scrid', 'read');
        }
      };
      addLoyaltyIdentifierFromScrid();
    }
    // adding addLoyaltyIdentifier to deps array will cause a non-terminating test
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale, payback.enabled, dispatch]);

  if (!payback?.enabled || basePoints === null) {
    return null;
  }

  return (
    <div className="bg-color-payback-light border-color-grey m-1.5 flex rounded border p-2 md:m-0">
      {/* logo  */}
      <div className="pr-2 pt-1">
        <PaybackColored32 />
      </div>
      <div className="w-full">
        {/* headline */}
        <div className="flex w-full justify-between">
          <Typography variant="body1" sx={{ fontWeight: 'fontWeightSemiBold', mb: '0.3rem' }}>
            {formatMessage(messages.headline)}
          </Typography>
          {basePoints >= 0 && connected && (
            <Typography
              variant="body1"
              sx={{ fontWeight: 'fontWeightSemiBold' }}
              data-testid="Bold-Points"
            >
              {state.fetching ? (
                <Skeleton sx={{ display: 'inline-block', width: '3rem', mr: 0.5 }} />
              ) : (
                <FormattedNumber value={basePoints} />
              )}
              °P
            </Typography>
          )}
        </div>

        {/* Messages */}
        {basePoints >= 0 && !connected && (
          <>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {formatMessage(messages.defaultDescription, {
                points: (
                  <Typography
                    component="span"
                    fontWeight="fontWeightSemiBold"
                    variant="body2"
                    marginLeft="0.25rem"
                  >
                    <FormattedNumber value={basePoints} /> °P
                  </Typography>
                ),
              })}
            </Typography>
            <Link
              sx={{
                color: 'text.darkTransparent',
                textDecorationColor: 'currentcolor',
                ':hover': { color: 'text.dark' },
              }}
              href={payback.link ?? ''}
              target="_blank"
            >
              <Typography variant="body2">{formatMessage(messages.loginLink)}</Typography>
            </Link>
          </>
        )}
        {connected && (
          <div className="flex items-center">
            <div className="mr-1">
              <CheckmarkInverted20
                sx={{ width: '0.9375rem', height: '0.9375rem', color: 'success.main' }}
              />
            </div>
            <div>
              <Typography variant="body2" sx={{ color: 'success.main' }}>
                {formatMessage(messages.cardPresent)}
              </Typography>
            </div>
          </div>
        )}
        {mutationResult && !mutationResult.success && (
          <InlineNotification severity="warning">
            {formatMessage(messages.cardNumberWarningMsg)}
          </InlineNotification>
        )}
      </div>
    </div>
  );
};
